// src/pages/DevicesPage.jsx
import { 
  FaSignal, 
  FaNetworkWired, 
  FaWifi, 
  FaSatellite, 
  FaShoppingCart, 
  FaPlus, 
  FaMinus, 
  FaTrash, 
  FaTimes, 
  FaPhone, 
  FaLock, 
  FaCheckCircle, 
  FaSpinner, 
  FaCreditCard,
  FaMicrochip,
  FaPlug,
  FaBolt,
  FaArrowRight
} from 'react-icons/fa';
import Layout, { useLanguage } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import CheckoutModal from '../components/CheckoutModal';

export default function DevicesPage() {
  const { t } = useLanguage();
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ─── Device Categories with Translations ───
  const deviceCategories = [
    {
      id: 'routers',
      title: t('devices.category.routers') || 'Routers & Gateways',
      icon: FaNetworkWired,
      description: t('devices.category.routersDesc') || 'High-performance routers for seamless connectivity throughout your home or office.',
      devices: [
        {
          id: 1,
          name: t('devices.routerPro.name') || 'Nexcom Router Pro',
          description: t('devices.routerPro.description') || 'Dual-band WiFi 6 router with advanced security features',
          price: 4500,
          priceFormatted: 'KES 4,500',
          features: [
            t('devices.routerPro.feature1') || 'Dual-band WiFi',
            t('devices.routerPro.feature2') || 'Up to 300Mbps',
            t('devices.routerPro.feature3') || 'Covers 1500 sq ft',
            t('devices.routerPro.feature4') || 'Supports 32 devices'
          ],
          icon: FaSignal,
          badge: t('devices.routerPro.badge') || 'Best Seller',
          badgeColor: '#f97316',
          imagePlaceholder: 'Router'
        },
        {
          id: 2,
          name: t('devices.routerLite.name') || 'Nexcom Router Lite',
          description: t('devices.routerLite.description') || 'Affordable router for basic home connectivity needs',
          price: 2999,
          priceFormatted: 'KES 2,999',
          features: [
            t('devices.routerLite.feature1') || 'Single-band WiFi',
            t('devices.routerLite.feature2') || 'Up to 150Mbps',
            t('devices.routerLite.feature3') || 'Covers 800 sq ft',
            t('devices.routerLite.feature4') || 'Supports 16 devices'
          ],
          icon: FaSignal,
          badge: t('devices.routerLite.badge') || 'Budget',
          badgeColor: '#14b8a6',
          imagePlaceholder: 'Router'
        }
      ]
    },
    {
      id: 'extenders',
      title: t('devices.category.extenders') || 'Extenders & Boosters',
      icon: FaPlug,
      description: t('devices.category.extendersDesc') || 'Extend your WiFi coverage to every corner of your space with these powerful boosters.',
      devices: [
        {
          id: 4,
          name: t('devices.extenderPlus.name') || 'Nexcom Extender Plus',
          description: t('devices.extenderPlus.description') || 'High-gain WiFi extender for large homes and offices',
          price: 3200,
          priceFormatted: 'KES 3,200',
          features: [
            t('devices.extenderPlus.feature1') || 'Plug and play',
            t('devices.extenderPlus.feature2') || 'Up to 300Mbps',
            t('devices.extenderPlus.feature3') || 'Covers 1000 sq ft',
            t('devices.extenderPlus.feature4') || 'Easy setup'
          ],
          icon: FaNetworkWired,
          badge: t('devices.extenderPlus.badge') || 'Value Pick',
          badgeColor: '#f97316cc',
          imagePlaceholder: 'Extender'
        },
        {
          id: 5,
          name: t('devices.extenderMini.name') || 'Nexcom Extender Mini',
          description: t('devices.extenderMini.description') || 'Compact WiFi booster for smaller spaces',
          price: 1999,
          priceFormatted: 'KES 1,999',
          features: [
            t('devices.extenderMini.feature1') || 'Plug and play',
            t('devices.extenderMini.feature2') || 'Up to 150Mbps',
            t('devices.extenderMini.feature3') || 'Covers 500 sq ft',
            t('devices.extenderMini.feature4') || 'Compact design'
          ],
          icon: FaNetworkWired,
          badge: t('devices.extenderMini.badge') || 'Compact',
          badgeColor: '#14b8a6',
          imagePlaceholder: 'Extender'
        }
      ]
    },
    {
      id: 'mobile',
      title: t('devices.category.mobile') || 'Mobile & Portable',
      icon: FaWifi,
      description: t('devices.category.mobileDesc') || 'Portable connectivity solutions for internet on the go.',
      devices: [
        {
          id: 7,
          name: t('devices.mifi5g.name') || 'Nexcom MiFi 5G',
          description: t('devices.mifi5g.description') || 'Next-gen portable hotspot with 5G connectivity',
          price: 5500,
          priceFormatted: 'KES 5,500',
          features: [
            t('devices.mifi5g.feature1') || '4G LTE/5G',
            t('devices.mifi5g.feature2') || 'Up to 12 hours battery',
            t('devices.mifi5g.feature3') || 'Connects 16 devices',
            t('devices.mifi5g.feature4') || 'Pocket-sized'
          ],
          icon: FaWifi,
          badge: t('devices.mifi5g.badge') || 'Portable',
          badgeColor: '#f97316b3',
          imagePlaceholder: 'MiFi'
        },
        {
          id: 8,
          name: t('devices.mifi4g.name') || 'Nexcom MiFi 4G',
          description: t('devices.mifi4g.description') || 'Reliable 4G LTE portable hotspot for daily use',
          price: 3200,
          priceFormatted: 'KES 3,200',
          features: [
            t('devices.mifi4g.feature1') || '4G LTE',
            t('devices.mifi4g.feature2') || 'Up to 8 hours battery',
            t('devices.mifi4g.feature3') || 'Connects 10 devices',
            t('devices.mifi4g.feature4') || 'Lightweight'
          ],
          icon: FaWifi,
          badge: t('devices.mifi4g.badge') || 'Compact',
          badgeColor: '#14b8a6',
          imagePlaceholder: 'MiFi'
        }
      ]
    }
  ];

  // ─── Flatten devices for cart functionality ───
  const allDevices = deviceCategories.flatMap(cat => cat.devices);

  useEffect(() => {
    const savedCart = localStorage.getItem('nexcom_cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        setCart([]);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('nexcom_cart', JSON.stringify(cart));
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  }, [cart]);

  useEffect(() => {
    const total = cart.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(total);
  }, [cart]);

  useEffect(() => {
    const handleOpenCart = () => {
      setIsCartOpen(true);
    };
    window.addEventListener('openCart', handleOpenCart);
    return () => window.removeEventListener('openCart', handleOpenCart);
  }, []);

  const addToCart = (device) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === device.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === device.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...device, quantity: 1 }];
    });
  };

  const removeFromCart = (deviceId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== deviceId));
  };

  const updateQuantity = (deviceId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(deviceId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === deviceId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const clearCart = () => {
    setCart([]);
  };

  // ─── CHECKOUT: Open in iframe modal ───
  const handleCheckout = () => {
    if (cart.length === 0) return;
    
    const cartItems = cart.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      priceFormatted: item.priceFormatted
    }));
    
    const totalAmount = getTotal();
    setCheckoutData({
      items: cartItems,
      total: totalAmount
    });
    setIsCheckoutOpen(true);
    setIsCartOpen(false);
  };

  // ─── Translations ───
  const pageTitle = t('devices.pageTitle') || 'Power Your Connection.';
  const pageSubtitle = t('devices.pageSubtitle') || 'High-performance networking devices designed for speed and reliability. Get connected with Nexcom\'s premium equipment.';
  const ctaTitle = t('devices.ctaTitle') || 'Ready to Get Connected?';
  const ctaText = t('devices.ctaText') || 'Purchase your device today and get started with Nexcom\'s high-speed internet.';
  const ctaShop = t('devices.ctaShop') || 'Shop Now';
  const ctaContact = t('devices.ctaContact') || 'Contact Sales';

  // ─── Cart Translations ───
  const cartTitle = t('devices.cart.title') || 'Shopping Cart';
  const cartEmpty = t('devices.cart.empty') || 'Your cart is empty';
  const cartEmptySub = t('devices.cart.emptySub') || 'Start adding devices!';
  const cartTotal = t('devices.cart.total') || 'Total:';
  const cartClear = t('devices.cart.clear') || 'Clear';
  const cartCheckout = t('devices.cart.checkout') || 'Checkout';
  const cartLoginMessage = t('devices.cart.loginMessage') || 'Complete your purchase securely.';

  // ─── Styles ───
  const cartButtonStyles = {
    position: 'relative',
    background: 'transparent',
    border: 'none',
    color: '#a1a1aa',
    fontSize: '1.25rem',
    cursor: 'pointer',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const cartBadgeStyles = {
    position: 'absolute',
    top: '-4px',
    right: '-6px',
    background: '#f97316',
    color: '#ffffff',
    fontSize: '0.6rem',
    fontWeight: 700,
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const cardHoverStyles = {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(30, 30, 40, 0.4)',
    backdropFilter: 'blur(10px)',
  };

  const addToCartButtonStyles = {
    background: '#f97316',
    color: '#ffffff',
    padding: '0.5rem 1.2rem',
    borderRadius: '12px',
    border: 'none',
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontFamily: 'inherit',
  };

  // ─── Device Image Placeholder ───
  const DeviceImagePlaceholder = ({ label, size = 'large' }) => {
    const dimensions = size === 'large' 
      ? { width: isMobile ? 120 : 160, height: isMobile ? 120 : 160 }
      : { width: isMobile ? 80 : 100, height: isMobile ? 80 : 100 };
    
    return (
      <div style={{
        width: dimensions.width,
        height: dimensions.height,
        borderRadius: '12px',
        background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08), rgba(249, 115, 22, 0.02))',
        border: '1px solid rgba(249, 115, 22, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'all 0.3s ease',
        flexShrink: 0,
      }}>
        <div style={{
          fontSize: size === 'large' ? '2.5rem' : '1.8rem',
          color: 'rgba(249, 115, 22, 0.3)',
        }}>
          <FaSignal />
        </div>
        <span style={{
          fontSize: size === 'large' ? '0.6rem' : '0.5rem',
          color: 'rgba(255,255,255,0.2)',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {label || 'Device'}
        </span>
      </div>
    );
  };

  // ─── Category Card ───
  const CategoryCard = ({ category, index }) => {
    const Icon = category.icon;
    const [expanded, setExpanded] = useState(isMobile ? false : true);

    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        style={{
          marginBottom: isMobile ? '2rem' : '3rem',
        }}
      >
        {/* Category Header */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
            cursor: isMobile ? 'pointer' : 'default',
          }}
          onClick={() => isMobile && setExpanded(!expanded)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'rgba(249, 115, 22, 0.1)',
              borderRadius: '12px',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icon style={{ color: '#f97316', fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
            </div>
            <div>
              <h3 style={{
                fontSize: isMobile ? '1.2rem' : '1.5rem',
                fontWeight: 700,
                color: '#ffffff',
                fontFamily: 'var(--font-display)',
              }}>
                {category.title}
              </h3>
              <p style={{
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                color: '#a1a1aa',
                fontFamily: 'var(--font-body)',
                marginTop: '0.1rem',
              }}>
                {category.description}
              </p>
            </div>
          </div>
          {isMobile && (
            <span style={{
              color: '#a1a1aa',
              fontSize: '0.8rem',
              transition: 'transform 0.3s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
              ▼
            </span>
          )}
        </div>

        {/* Category Devices Grid */}
        <AnimatePresence>
          {(expanded || !isMobile) && (
            <motion.div
              initial={isMobile ? { opacity: 0, height: 0 } : { opacity: 1 }}
              animate={isMobile ? { opacity: 1, height: 'auto' } : { opacity: 1 }}
              exit={isMobile ? { opacity: 0, height: 0 } : { opacity: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))',
                gap: isMobile ? '1rem' : '1.5rem',
                overflow: 'hidden',
              }}
            >
              {category.devices.map((device, deviceIndex) => {
                const DeviceIcon = device.icon;
                return (
                  <motion.div
                    key={device.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: deviceIndex * 0.08 }}
                    style={{
                      ...cardHoverStyles,
                      padding: isMobile ? '1rem' : '1.5rem',
                      borderRadius: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.borderColor = 'rgba(249, 115, 22, 0.3)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(249, 115, 22, 0.08)';
                      e.currentTarget.style.background = 'rgba(30, 30, 40, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.background = 'rgba(30, 30, 40, 0.4)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      justifyContent: 'space-between',
                    }}>
                      <DeviceImagePlaceholder label={device.badge || 'Device'} size="small" />
                      <div style={{ flex: 1 }}>
                        <span style={{
                          display: 'inline-block',
                          background: device.badgeColor,
                          color: '#ffffff',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.5rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.3rem',
                        }}>
                          {device.badge}
                        </span>
                        <h4 style={{
                          fontSize: isMobile ? '1rem' : '1.1rem',
                          fontWeight: 700,
                          color: '#ffffff',
                          fontFamily: 'var(--font-display)',
                          marginBottom: '0.2rem',
                        }}>
                          {device.name}
                        </h4>
                        <p style={{
                          fontSize: isMobile ? '0.75rem' : '0.85rem',
                          color: '#a1a1aa',
                          fontFamily: 'var(--font-body)',
                          marginBottom: '0.5rem',
                        }}>
                          {device.description}
                        </p>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          marginBottom: '0.5rem',
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr',
                          gap: '0.15rem',
                        }}>
                          {device.features.slice(0, 3).map((feature, idx) => (
                            <li key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                              color: '#a1a1aa',
                              fontFamily: 'var(--font-body)',
                            }}>
                              <span style={{ color: '#f97316', fontSize: '0.6rem' }}>•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid rgba(255,255,255,0.04)',
                    }}>
                      <span style={{
                        fontSize: isMobile ? '1rem' : '1.2rem',
                        fontWeight: 700,
                        color: '#f97316',
                        fontFamily: 'var(--font-display)',
                      }}>
                        {device.priceFormatted}
                      </span>
                      <button 
                        onClick={() => addToCart(device)}
                        style={{
                          ...addToCartButtonStyles,
                          padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1.2rem',
                          fontSize: isMobile ? '0.7rem' : '0.85rem',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.background = '#ea580c';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(249, 115, 22, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.background = '#f97316';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <FaShoppingCart size={isMobile ? 10 : 12} /> {t('devices.addToCart') || 'Add to Cart'}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <Layout>
      {/* Mobile Cart Button */}
      <div style={{
        position: 'fixed',
        bottom: '5rem',
        right: '1rem',
        zIndex: 999,
        display: isMobile ? 'flex' : 'none',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <button
          style={cartButtonStyles}
          onClick={() => setIsCartOpen(!isCartOpen)}
          onMouseEnter={(e) => e.currentTarget.style.color = '#f97316'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#a1a1aa'}
        >
          <FaShoppingCart size={22} />
          {cartCount > 0 && (
            <span style={cartBadgeStyles}>{cartCount}</span>
          )}
        </button>
      </div>

      {/* Cart Sidebar */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
                zIndex: 999,
              }}
              onClick={() => setIsCartOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: isMobile ? '100%' : '400px',
                maxWidth: '100vw',
                height: '100vh',
                background: '#0a0a0a',
                borderLeft: '1px solid rgba(255,255,255,0.06)',
                zIndex: 1000,
                padding: isMobile ? '1rem' : '2rem 1.5rem',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}>
                <h2 style={{
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  fontFamily: 'var(--font-display)',
                }}>
                  {cartTitle}
                </h2>
                <button
                  onClick={() => setIsCartOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#a1a1aa',
                    fontSize: isMobile ? '1.25rem' : '1.5rem',
                    cursor: 'pointer',
                    transition: 'color 0.3s ease',
                    padding: '0.5rem',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#f97316'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#a1a1aa'}
                >
                  ✕
                </button>
              </div>

              {cart.length === 0 ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#a1a1aa',
                }}>
                  <FaShoppingCart size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                  <p style={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>{cartEmpty}</p>
                  <p style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', marginTop: '0.5rem' }}>{cartEmptySub}</p>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {cart.map((item) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        style={{
                          background: 'rgba(30,30,40,0.4)',
                          borderRadius: '12px',
                          padding: isMobile ? '0.75rem' : '1rem',
                          marginBottom: '0.75rem',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{
                              fontSize: isMobile ? '0.85rem' : '0.95rem',
                              fontWeight: 600,
                              color: '#ffffff',
                              marginBottom: '0.25rem',
                              fontFamily: 'var(--font-display)',
                            }}>
                              {item.name}
                            </h4>
                            <p style={{
                              fontSize: isMobile ? '0.75rem' : '0.85rem',
                              color: '#f97316',
                              fontWeight: 700,
                            }}>
                              {item.priceFormatted}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#a1a1aa',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#a1a1aa'}
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginTop: '0.5rem',
                        }}>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: 'none',
                              color: '#a1a1aa',
                              width: isMobile ? '24px' : '28px',
                              height: isMobile ? '24px' : '28px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(249,115,22,0.2)';
                              e.currentTarget.style.color = '#f97316';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                              e.currentTarget.style.color = '#a1a1aa';
                            }}
                          >
                            <FaMinus size={10} />
                          </button>
                          <span style={{
                            fontSize: isMobile ? '0.8rem' : '0.9rem',
                            fontWeight: 600,
                            color: '#ffffff',
                            minWidth: '24px',
                            textAlign: 'center',
                          }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: 'none',
                              color: '#a1a1aa',
                              width: isMobile ? '24px' : '28px',
                              height: isMobile ? '24px' : '28px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(249,115,22,0.2)';
                              e.currentTarget.style.color = '#f97316';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                              e.currentTarget.style.color = '#a1a1aa';
                            }}
                          >
                            <FaPlus size={10} />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div style={{
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    paddingTop: '1rem',
                    marginTop: '1rem',
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '1rem',
                    }}>
                      <span style={{ color: '#a1a1aa' }}>{cartTotal}</span>
                      <span style={{
                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                        fontWeight: 700,
                        color: '#f97316',
                      }}>
                        KES {getTotal().toLocaleString()}
                      </span>
                    </div>
                    
                    <div style={{
                      background: 'rgba(249, 115, 22, 0.08)',
                      border: '1px solid rgba(249, 115, 22, 0.15)',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                    }}>
                      <p style={{
                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                        color: '#f97316',
                        fontFamily: 'var(--font-body)',
                        textAlign: 'center',
                      }}>
                        {cartLoginMessage}
                      </p>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '0.75rem',
                      flexDirection: isMobile ? 'column' : 'row',
                    }}>
                      <button
                        onClick={clearCart}
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: '#a1a1aa',
                          padding: isMobile ? '0.6rem' : '0.75rem',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          transition: 'all 0.3s ease',
                          fontFamily: 'inherit',
                          fontSize: isMobile ? '0.85rem' : '1rem',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#ef4444';
                          e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.color = '#a1a1aa';
                        }}
                      >
                        {cartClear}
                      </button>
                      <button
                        onClick={handleCheckout}
                        style={{
                          flex: 2,
                          background: '#f97316',
                          color: '#ffffff',
                          border: 'none',
                          padding: isMobile ? '0.6rem' : '0.75rem',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontWeight: 700,
                          transition: 'all 0.3s ease',
                          fontFamily: 'inherit',
                          fontSize: isMobile ? '0.85rem' : '1rem',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#ea580c';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(249,115,22,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f97316';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {cartCheckout}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ─── CHECKOUT IFRAME MODAL ─── */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cartData={checkoutData?.items || []}
        totalAmount={checkoutData?.total || 0}
      />

      {/* ─── Main Page Content ─── */}
      <div className="page-container">
        <div className="page-content">
          {/* Hero Section */}
          <section style={{ marginBottom: isMobile ? '2rem' : '4rem', position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '48rem' }}>
              <span style={{ 
                display: 'inline-block',
                color: '#f97316',
                fontFamily: 'var(--font-primary)',
                fontSize: isMobile ? '0.55rem' : '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase'
              }}>
                {t('devices.eyebrow') || 'Equipment & Accessories'}
              </span>
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: isMobile ? 'clamp(2rem, 10vw, 2.8rem)' : 'clamp(2.5rem, 5vw, 4.5rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                color: '#ffffff'
              }}>
                {pageTitle}
              </h1>
              <p style={{
                fontSize: isMobile ? '0.9rem' : 'clamp(1rem, 1.2vw, 1.25rem)',
                color: '#a1a1aa',
                maxWidth: '560px',
                lineHeight: 1.8,
                marginTop: '0.5rem',
                fontFamily: 'var(--font-body)'
              }}>
                {pageSubtitle}
              </p>
            </div>
          </section>

          {/* ─── Device Categories ─── */}
          <section style={{ marginBottom: isMobile ? '3rem' : '5rem' }}>
            {deviceCategories.map((category, index) => (
              <CategoryCard key={category.id} category={category} index={index} />
            ))}
          </section>

          {/* ─── CTA Section ─── */}
          <section style={{ margin: isMobile ? '3rem 0' : '6rem 0' }}>
            <div style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: isMobile ? '16px' : '24px',
              background: '#ea580c',
              padding: isMobile ? '2rem 1rem' : '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                position: 'absolute',
                right: 0,
                top: 0,
                padding: '2rem',
                opacity: 0.05
              }}>
                <FaSignal style={{ fontSize: isMobile ? '80px' : '160px', color: '#ffffff' }} />
              </div>
              <h2 style={{
                position: 'relative',
                zIndex: 10,
                fontSize: isMobile ? 'clamp(1.5rem, 5vw, 2rem)' : 'clamp(2rem, 3vw, 3rem)',
                fontWeight: 800,
                color: '#ffffff',
                marginBottom: isMobile ? '1rem' : '1.5rem',
                fontFamily: 'var(--font-display)'
              }}>
                {ctaTitle}
              </h2>
              <p style={{
                position: 'relative',
                zIndex: 10,
                maxWidth: '600px',
                margin: '0 auto ' + (isMobile ? '1.5rem' : '2rem'),
                fontSize: isMobile ? '0.95rem' : '1.1rem',
                color: 'rgba(255,255,255,0.8)',
                fontFamily: 'var(--font-body)'
              }}>
                {ctaText}
              </p>
              <div style={{
                position: 'relative',
                zIndex: 10,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '1rem',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <button 
                  onClick={() => window.open('https://nexcom.onelynq.app/', '_blank')}
                  style={{
                    background: '#ffffff',
                    color: '#ea580c',
                    padding: isMobile ? '1rem 2rem' : '1.25rem 3rem',
                    borderRadius: '16px',
                    border: 'none',
                    fontWeight: 800,
                    fontSize: isMobile ? '0.95rem' : '1.1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    maxWidth: '300px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    fontFamily: 'var(--font-primary)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  {ctaShop}
                </button>
                <button 
                  onClick={() => window.location.href = '/contact'}
                  style={{
                    background: 'transparent',
                    color: '#ffffff',
                    padding: isMobile ? '1rem 2rem' : '1.25rem 3rem',
                    borderRadius: '16px',
                    border: '2px solid rgba(255,255,255,0.4)',
                    fontWeight: 700,
                    fontSize: isMobile ? '0.95rem' : '1.1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    maxWidth: '300px',
                    fontFamily: 'var(--font-primary)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
                  }}
                >
                  {ctaContact}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Layout>
  );
}